<template>
  <div class="voice-ai-trigger">
    <v-btn
      color="primary"
      size="small"
      :variant="buttonVariant"
      :rounded="buttonVariant === 'elevated' ? undefined : 'sm'"
      prepend-icon="mdi-phone-in-talk"
      :disabled="!resolvedConfig?.isEnabled"
      @click.stop="openDialog">
      {{ label }}
    </v-btn>
    <span v-if="!resolvedConfig?.isEnabled" class="voice-ai-trigger__hint">Voice AI not enabled for your tenant.</span>

    <v-dialog v-model="open" max-width="540" @click:outside="open = false">
      <v-card class="voice-ai-trigger__card" rounded="sm" @click.stop>
        <div class="voice-ai-trigger__head">
          <span class="voice-ai-trigger__eyebrow">Voice AI</span>
          <span class="voice-ai-trigger__title">Outbound call</span>
          <span v-if="patientLabel" class="voice-ai-trigger__patient">
            {{ patientLabel
            }}<span v-if="patientDobLabel" class="voice-ai-trigger__mono"> · DOB {{ patientDobLabel }}</span>
          </span>
        </div>
        <v-card-text class="voice-ai-trigger__body">
          <v-select
            v-model="templateKey"
            :items="supportedTemplates"
            item-title="label"
            item-value="key"
            label="Script"
            variant="outlined"
            density="compact"
            hide-details
            class="mb-2" />

          <v-text-field
            v-model="toNumber"
            :label="toNumberLabel"
            variant="outlined"
            density="compact"
            :loading="prefilling"
            class="voice-ai-trigger__mono mb-2"
            hide-details="auto" />

          <div
            v-for="(fieldRow, i) in fieldRows"
            :key="`${templateKey}-${i}`"
            :class="{ 'd-flex': fieldRow.length > 1 }"
            style="gap: 8px">
            <template v-for="field in fieldRow" :key="field.key">
              <v-textarea
                v-if="field.input === 'textarea'"
                v-model="values[field.key]"
                :label="field.label"
                :placeholder="field.placeholder"
                variant="outlined"
                density="compact"
                rows="2"
                auto-grow
                hide-details="auto"
                class="mb-2" />
              <v-text-field
                v-else
                v-model="values[field.key]"
                :label="field.label"
                :placeholder="field.placeholder"
                variant="outlined"
                density="compact"
                hide-details="auto"
                :class="[isMono(field) ? 'voice-ai-trigger__mono' : '', 'mb-2']" />
            </template>
          </div>

          <p v-if="missingFields.length" class="voice-ai-trigger__hint mb-2">
            Fill in before calling: {{ missingFields.join(", ") }}
          </p>

          <v-radio-group v-model="sendMode" hide-details inline class="mb-2">
            <v-radio label="Send now" value="now" />
            <v-radio label="Schedule for later" value="later" />
          </v-radio-group>

          <v-text-field
            v-if="sendMode === 'later'"
            v-model="scheduledFor"
            type="datetime-local"
            label="When"
            variant="outlined"
            density="compact"
            hide-details />
        </v-card-text>

        <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mx-4 mb-2">{{ error }}</v-alert>

        <div class="voice-ai-trigger__foot">
          <v-btn variant="text" size="small" rounded="sm" @click="open = false">Cancel</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            size="small"
            rounded="sm"
            :loading="loading"
            :disabled="!canSubmit"
            @click="submit">
            {{ sendMode === "now" ? "Call now" : "Schedule" }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { zonedLocalToUtcIso } from "@/utils/dateUtils";
import { convertDateToMMDDYYYY } from "@/utils";

import { anchorPayload, anchorQueryParams, offeredTemplates } from "@/components/voice_ai/anchors";
import { useVoiceAI } from "@/composables/useVoiceAI";
import { voiceAiService } from "@/services/voiceAiService";

const props = defineProps({
  // The one case row the call is placed from: { type: "order_info" | "intake_order" | "intake_document", id }.
  anchor: { type: Object, required: true },
  // Where this card is mounted; only templates declaring the surface are offered.
  surface: { type: String, default: "PRIOR_AUTH" },
  // Fallback header/prefill when the context lookup is unavailable: { firstName, lastName, dob }.
  patient: { type: Object, default: null },
  // When provided (PA table), config/templates come from a single page-level
  // fetch (LAM-4503) and this component fetches nothing on mount.
  config: { type: Object, default: undefined },
  templates: { type: Array, default: undefined },
  label: { type: String, default: "Voice AI Call" },
  buttonVariant: { type: String, default: "elevated" },
});
const emit = defineEmits(["call-triggered"]);

const { getConfig, listTemplates, triggerCall, loading } = useVoiceAI();

const open = ref(false);
const error = ref("");
const fetchedConfig = ref(null);
const fetchedTemplates = ref(null);
const resolvedConfig = computed(() => (props.config !== undefined ? props.config : fetchedConfig.value));

// The form renders each template's backend-declared ui_fields, so any template the backend
// returns is offered — no per-template frontend code. The list is narrowed to templates the
// tenant has provisioned an assistant for and that declare this surface.
const allTemplates = computed(() => (props.templates !== undefined ? props.templates : fetchedTemplates.value) || []);
const supportedTemplates = computed(() => offeredTemplates(allTemplates.value, props.surface));

const prefilling = ref(false);
const context = ref(null);
let contextRequest = 0;
const toNumber = ref("");
const autoSeededToNumber = ref("");
const values = ref({});
const templateKey = ref("");
const sendMode = ref("now");
const scheduledFor = ref("");

const patientLabel = computed(() => {
  const first = context.value?.patientFirstName || props.patient?.firstName || "";
  const last = context.value?.patientLastName || props.patient?.lastName || "";
  return `${first} ${last}`.trim();
});
const patientDobLabel = computed(() => {
  const dob = context.value?.patientDob || props.patient?.dob || "";
  if (!dob) return "";
  try {
    return convertDateToMMDDYYYY(String(dob));
  } catch {
    return String(dob);
  }
});

const currentTemplate = computed(() => allTemplates.value.find((t) => t.key === templateKey.value) || null);
const fields = computed(() => currentTemplate.value?.uiFields || []);
const toNumberLabel = computed(() => currentTemplate.value?.toNumber?.label || "Phone (E.164, e.g. +14075551687)");

// Group consecutive half-width fields into shared rows.
const fieldRows = computed(() => {
  const rows = [];
  for (const field of fields.value) {
    const prev = rows[rows.length - 1];
    if (field.half && prev && prev[0].half && prev.length < 2) prev.push(field);
    else rows.push([field]);
  }
  return rows;
});

function isMono(field) {
  return field.input === "phone" || field.input === "mono";
}

function shortLabel(field) {
  return field.label.split(" (")[0];
}

const missingFields = computed(() =>
  fields.value.filter((f) => f.required && !String(values.value[f.key] || "").trim()).map(shortLabel)
);

const canSubmit = computed(() => {
  if (!templateKey.value) return false;
  if (!toNumber.value.trim()) return false;
  if (sendMode.value === "later" && !scheduledFor.value) return false;
  if (missingFields.value.length) return false;
  return true;
});

// Best-effort E.164 normalization for loosely-stored US phone numbers
// (e.g. "(407) 555-1687", "407-555-1687", "14075551687").
function toE164(raw) {
  if (!raw) return "";
  const trimmed = String(raw).trim();
  if (/^\+[1-9]\d{1,14}$/.test(trimmed)) return trimmed;
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return "";
}

// ui_fields carry snake_case source keys (backend contract); context/config responses are camelized.
function snakeToCamel(s) {
  return String(s).replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());
}

function prefillValue(field) {
  // prefill_from may be a single context key or an ordered fallback chain.
  const sources = Array.isArray(field.prefillFrom) ? field.prefillFrom : field.prefillFrom ? [field.prefillFrom] : [];
  for (const source of sources) {
    const fromContext = context.value?.[snakeToCamel(source)];
    if (fromContext) return String(fromContext);
  }
  if (field.prefillConfig) return String(resolvedConfig.value?.[snakeToCamel(field.prefillConfig)] || "");
  return "";
}

// Fill empty fields from the case context + tenant config; never overwrite user-typed values.
// The to-number is auto-seeded per template; switching templates replaces it only if untouched.
function applyPrefills() {
  for (const field of fields.value) {
    if (!String(values.value[field.key] || "").trim()) values.value[field.key] = prefillValue(field);
  }
  const seedSource = currentTemplate.value?.toNumber?.prefillFrom;
  const seed = seedSource ? toE164(context.value?.[snakeToCamel(seedSource)]) : "";
  if (!toNumber.value || toNumber.value === autoSeededToNumber.value) {
    toNumber.value = seed;
    autoSeededToNumber.value = seed;
  }
}

// Each open starts from a clean slate for the current anchor; a slower lookup from an earlier
// open (or another row) must never land on top of this one.
async function openDialog() {
  open.value = true;
  error.value = "";
  context.value = null;
  values.value = {};
  toNumber.value = "";
  autoSeededToNumber.value = "";
  prefilling.value = true;
  const request = ++contextRequest;
  let loaded = null;
  try {
    loaded = (await voiceAiService.getContext(anchorQueryParams(props.anchor))) || null;
  } catch {
    // Non-fatal: handled by the patient fallback below.
  }
  if (request !== contextRequest) return;
  context.value = loaded || {
    patientFirstName: props.patient?.firstName || "",
    patientLastName: props.patient?.lastName || "",
    patientDob: props.patient?.dob || "",
  };
  applyPrefills();
  prefilling.value = false;
}

watch(templateKey, () => applyPrefills());

watch(
  supportedTemplates,
  (list) => {
    if (!list.some((t) => t.key === templateKey.value)) {
      templateKey.value = list[0]?.key ?? "";
    }
  },
  { immediate: true }
);

onMounted(async () => {
  if (props.config !== undefined && props.templates !== undefined) return;
  try {
    if (props.config === undefined) fetchedConfig.value = await getConfig();
    if (props.templates === undefined) fetchedTemplates.value = await listTemplates();
  } catch (err) {
    error.value = err?.message || "Failed to load Voice AI config";
  }
});

async function submit() {
  error.value = "";
  try {
    const variableValues = {
      agent_name: "Ada",
      tenant_display_name: resolvedConfig.value?.callerIdDisplayName || "Lamar Health",
    };
    for (const field of fields.value) {
      variableValues[field.key] = String(values.value[field.key] || "").trim() || field.fallback || "";
    }
    const payload = {
      ...anchorPayload(props.anchor),
      templateKey: templateKey.value,
      toPhoneNumber: toNumber.value.trim(),
      variableValues,
    };
    if (sendMode.value === "later" && scheduledFor.value) {
      payload.scheduledFor = zonedLocalToUtcIso(scheduledFor.value, resolvedConfig.value?.timezone);
    }
    const session = await triggerCall(payload);
    open.value = false;
    emit("call-triggered", session);
  } catch (err) {
    error.value = err?.message || "Failed to start call";
  }
}
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.voice-ai-trigger {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}
.voice-ai-trigger__card {
  border: 1px solid $lh_dash_border;
}
.voice-ai-trigger__head {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 14px 20px 12px;
  border-bottom: 1px solid $lh_dash_border;
}
.voice-ai-trigger__eyebrow {
  font-family: $lh_font_mono;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: $lh_color_blue_gray;
}
.voice-ai-trigger__title {
  font-size: 16px;
  font-weight: 600;
  color: $lh_color_navy_dark;
}
.voice-ai-trigger__patient {
  margin-top: 2px;
  font-size: 12.5px;
  color: $lh_color_blue_gray;
}
.voice-ai-trigger__body {
  padding: 16px 20px 8px !important;
}
.voice-ai-trigger__foot {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  padding: 10px 20px;
  border-top: 1px solid $lh_dash_border;
}
.voice-ai-trigger__mono,
.voice-ai-trigger__mono :deep(input) {
  font-family: "JetBrains Mono", monospace;
}
.voice-ai-trigger__hint {
  font-size: 11px;
  color: $lh_color_blue_gray;
  opacity: 0.7;
}
</style>
